import type React from "react";
import { useState, type FormEvent } from "react";
import { CreditCard, Clock, Send, Check } from "lucide-react";
import accessURL from "../../../components/utils/superAccess";
import { toast } from "react-toastify";

interface Duration {
  value: string;
  label: string;
}

const allDurations: Duration[] = [
  { value: "1month", label: "1 Month" },
  { value: "2months", label: "2 Months" },
  { value: "3months", label: "3 Months" },
  { value: "4months", label: "4 Months" },
  { value: "5months", label: "5 Months" },
  { value: "6months", label: "6 Months" },
  { value: "7months", label: "7 Months" },
  { value: "8months", label: "8 Months" },
  { value: "9months", label: "9 Months" },
  { value: "10months", label: "10 Months" },
  { value: "11months", label: "11 Months" },
  { value: "1year", label: "1 Year" },
];

const Price: React.FC = () => {
  const [selectedDuration, setSelectedDuration] = useState<string>(
    allDurations[0].value
  );
  const [amount, setAmount] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await accessURL.post(`payments/setPrice`, {
        duration: selectedDuration,
        amount: `${amount}`,
      });

      if (res.status === 200) {
        setIsSubmitting(false);
        setIsSuccess(true);

        // Show toast notification
        toast("Price has been set successfully");

        // Reset success message after 3 seconds and refresh page
        setTimeout(() => {
          setIsSuccess(false);
          setIsOpen(false);
          window.location.reload();
        }, 3000);
      }
      setIsSubmitting(false);
    } catch (error) {
      //   console.error("Error setting price:", error);
      setIsSubmitting(false);
      setIsSubmitting(false);
      // Show error toast
      toast.error("error setting price");
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="text-2xl font-bold px-6 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
      >
        Set Price
      </button>

      {/* Modal Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* Modal Content */}
          <div className="relative max-w-md w-full mx-auto">
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
              <div className="flex items-center justify-center mb-6">
                <h2 className="text-2xl font-bold text-black">Set Price</h2>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Duration Selection */}
                <div className="mb-6">
                  <label className="block text-black font-medium mb-2 flex items-center">
                    <Clock className="mr-2 text-black" size={20} />
                    Select Duration
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {allDurations.map((duration) => (
                      <button
                        key={duration.value}
                        type="button"
                        className={`py-2 px-3 text-sm rounded-md transition-colors ${
                          selectedDuration === duration.value
                            ? "bg-black text-white"
                            : "bg-gray-100 text-black hover:bg-gray-200"
                        }`}
                        onClick={() => setSelectedDuration(duration.value)}
                      >
                        {duration.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount Input */}
                <div className="mb-6">
                  <label className="block text-black font-medium mb-2 flex items-center">
                    <CreditCard className="mr-2 text-black" size={20} />
                    Enter Amount (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                     
                    </span>
                    <input
                      type="number"
                      className="w-full py-2 pl-8 pr-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex items-center justify-center py-3 px-4 rounded-md text-white font-medium transition-colors ${
                    isSubmitting ? "bg-gray-400" : "bg-black hover:bg-gray-800"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Set Price
                    </>
                  )}
                </button>
              </form>

              {/* Success Message */}
              {isSuccess && (
                <div className="mt-4 p-3 bg-gray-100 text-black rounded-md flex items-center">
                  <Check className="mr-2" size={18} />
                  Price successfully set!
                </div>
              )}

              {/* Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                <h3 className="font-medium text-black mb-2">Price Summary</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium text-black">
                    {
                      allDurations.find((d) => d.value === selectedDuration)
                        ?.label
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-black">
                    USD&nbsp;{amount || "0.00"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Price;
